import { createPortal } from 'react-dom';

// mui
import EventBusyIcon from '@mui/icons-material/EventBusy';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PushPinIcon from '@mui/icons-material/PushPin';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';

// controls
import JxMinimizableFormBase from '../../../../JxControls/JxForms/JxMinimizableFormBase';
import JxTitleBar from '../../../../JxControls/JxTitleBar/JxTitleBar';
import JxButton from '../../../../JxControls/JxButtons/JxButton';
import JxButton2 from '../../../../JxControls/JxButtons/JxButton';
import '../../../../JxControls/JxTheme.css';
import CmdBarEdit from './CmdBarEdit';
import './CommandBarWidget.css';

/**
 * CommandBarWidget — floating command input for YourLibraryTab.
 * Extends JxMinimizableFormBase: drag + bringToTop + Escape handling + minimize-to-taskbar.
 *
 * User types a query or slash command, presses Enter or clicks Execute.
 * CmdBarEdit handles command autocomplete + param hints.
 * Emits onCommand(value) — parent (YourLibraryTab) decides what to do.
 *
 * Pin — togglePin()/isPinned() come from JxFormBase, PINNED_OFFSET keeps this
 * window above non-pinned windows regardless of selection order. Client-side
 * only for now, no BE persistence (see JxFormBase doc).
 *
 * @param {boolean}  props.open
 * @param {function} props.onClose
 * @param {function} props.onCommand - called with the full input string on execute
 */
export default class CommandBarWidget extends JxMinimizableFormBase {
    constructor(props) {
        super(props);
        this.name = 'CommandBarWidget';
        this.schema = null;

        this.state = {
            ...this.state,
            value: '',
        };
    }

    // Literal, hardcoded default — no window.innerWidth centering math, no reading the
    // cache here (JxFormBase.componentDidMount already does that once, for every window).
    getDefaultGeo() {
        return { top: 880, left: 810, width: 300, height: 60 };
    }

    getObjectType() {
        return this.name;
    }

    componentDidUpdate(prevProps) {
        super.componentDidUpdate(prevProps);

        if (this.props.open && !prevProps.open) {
            this.bringToTop();
            document.getElementById('cbw-input')?.focus();
        }
    }

    handleClose = () => {
        this.props.onClose?.();
    };

    _handleChange = (value) => {
        this.setState({ value });
    };

    _handleExecute = (value) => {
        if (!value.trim()) return;
        this.props.onCommand?.(value.trim());
        this.setState({ value: '' });
    };

    getExecuteIcon() {
        return <EventBusyIcon sx={{ fontSize: 14, color: 'var(--j-muted)' }} />;
    }

    getTaskbarTitle() {
        return 'Command Bar';
    }

    /**
     * JxTitleBar actions array — single pin toggle for now.
     * Icon reflects current isPinned state, same PushPin/PushPinOutlined
     * pattern as ContentPanelHeader's note-pin toggle.
     */
    getTitleBarActions() {
        return [
            {
                icon: this.state.isPinned ? <PushPinIcon sx={{ fontSize: 14 }} /> : <PushPinOutlinedIcon sx={{ fontSize: 14 }} />,
                title: this.state.isPinned ? 'Unpin' : 'Pin on top',
                onClick: this.togglePin,
            },
        ];
    }

    render() {
        if (!this.props.open || this.isMinimized) return null;

        const { value } = this.state;

        const panel = (
            <div {...this.panelProps} style={this.panelStyle} className={`cbw-panel ${this.panelActiveClass} ${this.panelPinnedClass}`}>
                <JxTitleBar
                    id={`${this.name}_titlebar`}
                    title="Command Bar"
                    variant="toolwindow"
                    isDraggable={true}
                    dragHandleProps={this.dragHandleProps}
                    actions={this.getTitleBarActions()}
                    isMinimizable={true}
                    windowId={this.getId()}
                />

                <div id="cbw-body" className="cbw-body">
                    <CmdBarEdit id="cbw-input" value={value} onChange={this._handleChange} onExecute={this._handleExecute} />
                    <JxButton
                        //
                        // icon={this.getExecuteIcon()}
                        icon={<PlayArrowIcon sx={{ fontSize: 14, color: 'var(--j-muted)' }} />}
                        label="Execute"
                        onClick={() => this._handleExecute(value)}
                    />
                </div>

                <div id="cbw-body" className="cbw-body">
                    <JxButton label="Recent" onClick={() => this._handleExecute('__recent__')} />
                    <JxButton label="Patterns" onClick={() => this._handleExecute('pattern')} />
                    <JxButton label="Pinned" onClick={() => this._handleExecute('__pinned__')} />
                    <JxButton label="Protocol" onClick={() => this._handleExecute('protocol')} />
                    <JxButton label="Recipe" onClick={() => this._handleExecute('recept')} />
                    <JxButton label="Uml" onClick={() => this._handleExecute('/uml')} />
                </div>
            </div>
        );

        return createPortal(panel, document.body);
    }
}
